import { z } from "zod";
import { parseOrThrow } from "_shared/_utilities/zod-schema-parser";

export type NameJSON = z.infer<typeof NameSchema.NameJSONZ>;
export type NameParams = z.infer<typeof NameSchema.NameZ>;

export default class NameSchema {
  public static readonly NameJSONZ = z.object({
    firstName: z.string().nullish(),
    lastName: z.string().nullish(),
  });

  public static readonly NameZ = z.object({
    firstName: z.string().optional(),
    lastName: z.string().optional(),
  }).superRefine((data, ctx) => {
    const isFirstNameValid = data.firstName == undefined || data.firstName.trim().length > 0;
    const isLastNameValid = data.lastName == undefined || data.lastName.trim().length > 0;

    if (!data.firstName && !data.lastName) {
      ctx.addIssue({
        code: 'custom',
        message: "Either firstName, lastName or both must be provided.",
      });
    }

    if (!isFirstNameValid) {
      ctx.addIssue({
        code: 'custom',
        message: "firstName, if provided, cannot be an empty string.",
      });
    }

    if (!isLastNameValid) {
      ctx.addIssue({
        code: 'custom',
        message: "lastName, if provided, cannot be an empty string.",
      });
    }
  });

  public static validate(data: unknown, logError: boolean = true): NameParams {
    return parseOrThrow(
      NameSchema.NameZ,
      data,
      { 
        logError,
        schemaName: "NameSchema",
        operation: "validate",
        payloadSample: data 
      }
    );
  }

  public static validateJSON(data: unknown): NameJSON {
    return parseOrThrow(
      NameSchema.NameJSONZ,
      data,
      { 
        logError: true,
        schemaName: "NameSchema",
        operation: "validateJSON",
        payloadSample: data 
      }
    );
  }

  public static isValid(data: unknown): data is NameParams {
    try {
      NameSchema.validate(data);
      return true;
    } catch {
      return false;
    }
  }

  public static isValidJSON(data: unknown): data is NameJSON {
    try {
      NameSchema.validateJSON(data);
      return true;
    } catch {
      return false;
    }
  }

  public static deserialize(data: NameJSON): NameParams {
    const parsed = parseOrThrow(
      NameSchema.NameJSONZ,
      data,
      { 
        logError: true,
        schemaName: "NameSchema",
        operation: "deserialize",
        payloadSample: data 
      }
    );
    return NameSchema.codec.decode(parsed);
  }

  public static serialize(data: NameParams): NameJSON {
    const parsed = NameSchema.validate(data);
    return NameSchema.codec.encode(parsed);
  }

  public static readonly codec = z.codec(
    NameSchema.NameJSONZ,
    NameSchema.NameZ, {
    decode: (data) => ({
      firstName: data.firstName ?? undefined,
      lastName: data.lastName ?? undefined
    }),
    encode: (name) => ({
      firstName: name.firstName,
      lastName: name.lastName
    })
  });
}
